/**
 * The PERSONA variant of the per-agent workspace —
 * `/marketing-agents/a/:domain` (spec #44 §3).
 *
 * This EXTENDS the approved four-tab console rather than rebuilding it:
 * the specialist variant stays exactly where it was, at
 * `/marketing-agents/s/{key}`, and this is its persona sibling.
 *
 *   Now            — the timeline, the same renderer as the channel thread
 *   Soul & memory  — #42 §6, built there
 *   Approvals      — proposals filtered to this domain
 *   Setup & goal   — variant A, the provenance form (#47 §3), built there
 *
 * Polaris `Tabs`, inlined per `DataUploads.tsx`. Under jsdom they collapse
 * into a disclosure menu (`approvalsInbox.test.tsx:91`), so the tab state
 * is also driven from the URL — which is what makes a tab linkable, the
 * same property the approvals inbox already relies on.
 */

import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BlockStack, Page, Tabs, Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import { AgentThread } from '../components/workspace/AgentThread';
import { QuestionCard } from '../components/workspace/QuestionCard';
import { SetupGoalTab } from '../components/workspace/setup/SetupGoalTab';
import { SoulMemoryTab } from '../components/workspace/soul/SoulMemoryTab';
import { WorkspaceEmpty } from '../components/workspace/WorkspaceEmpty';
import { useWorkspace } from '../hooks/useWorkspace';

const TAB_IDS = ['now', 'soul', 'approvals', 'setup'] as const;
type TabId = (typeof TAB_IDS)[number];

export default function AgentWorkspace() {
  const { domain = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const {
    agents,
    questions,
    approveQuestion,
    declineQuestion,
    loading,
  } = useWorkspace();

  const requested = params.get('tab');
  const selected: TabId = TAB_IDS.includes(requested as TabId)
    ? (requested as TabId)
    : 'now';

  const agent = agents.find((a) => a.domain === domain);

  const agentNames = useMemo(() => {
    const names: Record<string, string> = {};
    agents.forEach((a) => {
      names[a.domain] = a.display_name;
    });
    return names;
  }, [agents]);

  const mine = questions.filter((q) => q.owner_domain === domain);

  const onSelect = useCallback(
    (index: number) => {
      const next = new URLSearchParams(params);
      next.set('tab', TAB_IDS[index]);
      setParams(next);
    },
    [params, setParams]
  );

  const tabs = [
    { id: 'now', content: 'Now' },
    { id: 'soul', content: 'Soul & memory' },
    { id: 'approvals', content: 'Approvals' },
    { id: 'setup', content: 'Setup & goal' },
  ];

  // An unknown domain says so. It does not render an empty agent, which
  // would look like a configured agent that has done nothing.
  if (!loading.rosters && agents.length > 0 && !agent) {
    return (
      <Page title="Agent">
        <Text as="p" tone="subdued">
          {domain} is not an agent in this workspace.
        </Text>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title={agent?.display_name ?? domain}
      subtitle="The persona view: what it did, who it is, what it is waiting on."
    >
      <BlockStack gap="300">
        <div>
          <Link to={`/marketing-agents?c=${domain}`}>Open in the workspace</Link>
        </div>
        <Tabs
          tabs={tabs}
          selected={TAB_IDS.indexOf(selected)}
          onSelect={onSelect}
        >
          <div style={{ paddingTop: 16 }}>
            {selected === 'now' ? <AgentThread domain={domain} /> : null}

            {selected === 'soul' ? <SoulMemoryTab domain={domain} /> : null}

            {selected === 'approvals' ? (
              <div data-testid="persona-approvals">
                {mine.length === 0 ? (
                  <WorkspaceEmpty kind="no-questions" />
                ) : (
                  mine.map((question) =>
                    question.proposal ? (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        agentNames={agentNames}
                        onApprove={(q) => void approveQuestion(q)}
                        onDecline={(q, reason) =>
                          void declineQuestion(q, reason)
                        }
                      />
                    ) : null
                  )
                )}
              </div>
            ) : null}

            {selected === 'setup' ? (
              <div data-testid="persona-setup">
                <SetupGoalTab domain={domain} />
              </div>
            ) : null}
          </div>
        </Tabs>
      </BlockStack>
    </Page>
  );
}
